import Image from "next/image"
import { Check, GraduationCap, Heart, Mail } from "lucide-react"

/*
 * Mini product mockups — one per journey card, showing what the family (or
 * clinic) actually receives. Shared by the clinic-facing home page journey
 * (GoodbyeJourneySection) and the owner-facing journey on /owners so both tell
 * the same before/during/after story in the same visual language.
 *
 * Same lightweight CSS-mockup approach as TechnologySection: no illustrations
 * to ship, and every mockup stays on the brand palette.
 */

export function MiniBooklets() {
  return (
    <div className="relative w-[180px] h-[92px]">
      <div className="absolute left-3 top-4 w-[92px] h-[72px] bg-white rounded-md shadow-md border border-[#e0ebf3] -rotate-6 p-2.5">
        <div className="w-6 h-1 rounded bg-[#9B7B4D] mb-1.5" />
        <div className="text-[9px] font-bold text-[#002e65] leading-tight">How will I know?</div>
        <div className="text-[7px] text-[#94a3b8] mt-0.5">Quality-of-life guide</div>
      </div>
      <div className="absolute right-3 top-1 w-[92px] h-[76px] bg-[#002e65] rounded-md shadow-lg rotate-3 p-2.5">
        <Heart className="w-3 h-3 text-white/80 mb-1" />
        <div className="text-[9px] font-bold text-white leading-tight">Your Pet Loss Guide</div>
        <div className="text-[7px] text-white/60 mt-0.5">For the whole family</div>
      </div>
    </div>
  )
}

export function MiniCPD() {
  return (
    <div className="w-[170px] bg-white rounded-lg shadow-md border border-[#e0ebf3] p-3 text-center">
      <div className="mx-auto w-8 h-8 rounded-full bg-[#e0ebf3] flex items-center justify-center mb-1.5">
        <GraduationCap className="w-4 h-4 text-[#004da9]" />
      </div>
      <div className="text-[10px] font-bold text-[#002e65]">End-of-Life Care CPD</div>
      <div className="text-[8px] text-[#94a3b8] mt-0.5">For the whole team</div>
      <div className="mt-1.5 inline-flex items-center gap-1 bg-[#dcfce7] rounded-full px-2 py-0.5">
        <Check className="w-2.5 h-2.5 text-[#16a34a]" strokeWidth={3} />
        <span className="text-[8px] font-semibold text-[#16a34a]">Certified</span>
      </div>
    </div>
  )
}

export function MiniPreArrival() {
  return (
    <div className="w-[176px] bg-white rounded-lg shadow-md border border-[#e0ebf3] overflow-hidden">
      <div className="bg-[#4a86c5] px-3 py-1.5">
        <div className="text-white text-[9px] font-bold">Bella&rsquo;s aftercare choices</div>
      </div>
      <div className="p-2.5 space-y-1.5">
        {["Heritage cremation", "Paw print keepsake"].map((label) => (
          <div key={label} className="flex items-center gap-1.5">
            <div className="w-3.5 h-3.5 rounded-full bg-[#4a86c5] flex items-center justify-center flex-shrink-0">
              <Check className="w-2 h-2 text-white" strokeWidth={3} />
            </div>
            <span className="text-[9px] font-semibold text-[#002e65]">{label}</span>
          </div>
        ))}
        <div className="inline-flex items-center gap-1 rounded-full bg-[#e6f0fa] px-2 py-0.5">
          <span className="w-1.5 h-1.5 rounded-full bg-[#4a86c5]" />
          <span className="text-[8px] font-semibold text-[#004da9]">Settled from home</span>
        </div>
      </div>
    </div>
  )
}

export function MiniBlanket() {
  // A soft folded blanket — the comfort-room staple families actually see.
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-[112px]">
        {/* folded layers, seen side-on */}
        <div className="h-[15px] rounded-t-2xl bg-[#c7dbee] border border-b-0 border-[#a6c4e0]" />
        <div className="h-[15px] bg-[#dbe8f4] border-x border-[#a6c4e0]" />
        <div className="h-[15px] bg-[#c7dbee] border-x border-[#a6c4e0]" />
        <div className="h-[17px] rounded-b-2xl bg-[#dbe8f4] border border-t-0 border-[#a6c4e0] shadow-md" />
        {/* stitched heart tag */}
        <div className="absolute -right-2.5 -top-2.5 w-7 h-7 rounded-full bg-white shadow-md border border-[#e0ebf3] flex items-center justify-center">
          <Heart className="w-3.5 h-3.5 text-[#9B7B4D] fill-[#9B7B4D]/25" />
        </div>
      </div>
      <div className="mt-2.5 inline-flex items-center gap-1 rounded-full bg-white shadow-sm border border-[#e0ebf3] px-2 py-0.5">
        <span className="text-[8px] font-semibold text-[#002e65]">Comfort room — never rushed</span>
      </div>
    </div>
  )
}

export function MiniBag() {
  return (
    <div className="flex flex-col items-center">
      <div className="relative">
        {/* handle */}
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 w-10 h-7 rounded-t-full border-4 border-[#5cb6dc] bg-transparent" />
        {/* bag body */}
        <div className="relative w-[96px] h-[62px] rounded-lg bg-[#7ecbe8] shadow-md border border-[#5cb6dc] flex items-center justify-center">
          <div className="bg-white/95 rounded px-1.5 py-1 text-center shadow-sm">
            <div className="text-[8px] font-bold text-[#1c789e] leading-none">clinicarebag</div>
            <div className="text-[6px] text-[#94a3b8] mt-0.5 leading-none">Bella</div>
          </div>
        </div>
      </div>
      <div className="mt-2.5 inline-flex items-center gap-1 rounded-full bg-white shadow-sm border border-[#e0ebf3] px-2 py-0.5">
        <Check className="w-2.5 h-2.5 text-[#16a34a]" strokeWidth={3} />
        <span className="text-[8px] font-semibold text-[#002e65]">Priced by bag size — no scales</span>
      </div>
    </div>
  )
}

export function MiniTracking() {
  const steps = [
    { label: "Collected from clinic", done: true },
    { label: "In our care", done: true },
    { label: "Ready to return home", done: false },
  ]
  return (
    <div className="w-[168px] bg-white rounded-lg shadow-md border border-[#e0ebf3] overflow-hidden">
      <div className="bg-[#002e65] px-3 py-1.5">
        <div className="text-white text-[9px] font-bold">Bella&rsquo;s Journey</div>
      </div>
      <div className="p-2.5 space-y-1.5">
        {steps.map((s) => (
          <div key={s.label} className="flex items-center gap-1.5">
            <div
              className={`w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0 ${
                s.done ? "bg-[#004da9]" : "bg-[#cfe0ef]"
              }`}
            >
              {s.done && <Check className="w-2 h-2 text-white" strokeWidth={3} />}
            </div>
            <span
              className={`text-[9px] leading-tight ${
                s.done ? "text-[#002e65] font-semibold" : "text-[#9bb4cb]"
              }`}
            >
              {s.label}
            </span>
          </div>
        ))}
        <div className="inline-flex items-center gap-1 rounded-full bg-[#e6f0fa] px-2 py-0.5">
          <span className="w-1.5 h-1.5 rounded-full bg-[#004da9] animate-pulse" />
          <span className="text-[8px] font-semibold text-[#004da9]">Step 14 of 23</span>
        </div>
      </div>
    </div>
  )
}

export function MiniLabel() {
  // An identity swing tag attached to a package — everything in our care is
  // labelled, the same discipline as labelling a drug.
  return (
    <div className="relative w-[180px] h-[92px]">
      {/* package */}
      <div className="absolute left-3 top-4 w-[86px] h-[58px] rounded-lg bg-[#c7d8e9] border border-[#a6bfd8] shadow-md overflow-hidden">
        {/* lid flap line */}
        <div className="absolute top-[16px] inset-x-0 h-[2px] bg-[#a6bfd8]" />
        <div className="absolute top-[3px] left-2 text-[7px] font-bold tracking-wide text-[#3a5a7c]">
          PETAFTERCARE
        </div>
      </div>
      {/* string from package corner to tag */}
      <div className="absolute left-[86px] top-[38px] w-[36px] h-[2px] bg-[#7b99b8] rotate-[24deg] origin-left rounded-full" />
      {/* swing tag */}
      <div className="absolute right-2 top-[40px] rotate-[6deg] bg-white rounded-md shadow-md border border-[#e0ebf3] pl-4 pr-2.5 py-1.5">
        {/* tag hole */}
        <span className="absolute left-1.5 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full border-[2px] border-[#9bb4cb]" />
        <div className="text-[9px] font-bold text-[#002e65] leading-tight">Bella · Wilson Family</div>
        <div className="flex items-center gap-1 mt-0.5">
          <Check className="w-2 h-2 text-[#16a34a]" strokeWidth={3} />
          <span className="text-[7px] font-semibold text-[#16a34a]">Identity verified</span>
        </div>
      </div>
    </div>
  )
}

export function MiniNote() {
  // The family's handwritten farewell note — read aloud to their pet by name.
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-[156px] bg-white rounded-md shadow-md border border-[#e0ebf3] px-3 py-2.5 -rotate-2">
        {/* tape */}
        <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-10 h-3 bg-[#f5e6b8]/80 rotate-2 rounded-sm" />
        <div
          className="text-[10px] italic text-[#3a5a7c] leading-snug"
          style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
        >
          &ldquo;Goodnight, sweet girl. Thank you for everything, Bella.&rdquo;
        </div>
        <div
          className="mt-1 text-[8px] italic text-[#94a3b8] text-right"
          style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
        >
          &mdash; The Wilson family
        </div>
      </div>
      <div className="mt-2.5 inline-flex items-center gap-1 rounded-full bg-white shadow-sm border border-[#e0ebf3] px-2 py-0.5">
        <Heart className="w-2.5 h-2.5 text-[#9B7B4D] fill-[#9B7B4D]/25" />
        <span className="text-[8px] font-semibold text-[#002e65]">Read aloud to Bella, by name</span>
      </div>
    </div>
  )
}

export function MiniEmail() {
  return (
    <div className="w-[176px] bg-white rounded-lg shadow-md border border-[#e0ebf3] overflow-hidden">
      <div className="bg-[#002e65] px-3 py-2 flex items-center gap-1.5">
        <div className="w-5 h-5 rounded bg-white/15 flex items-center justify-center">
          <Mail className="w-3 h-3 text-white" />
        </div>
        <div>
          <div className="text-white text-[9px] font-bold leading-tight">PetAftercare</div>
          <div className="text-white/70 text-[7px] leading-tight">Update on Bella&rsquo;s care</div>
        </div>
      </div>
      <div className="p-2.5 space-y-1.5">
        <div className="h-1.5 w-3/4 rounded bg-[#e6edf4]" />
        <div className="h-1.5 w-full rounded bg-[#eef2f6]" />
        <div className="inline-flex rounded bg-[#004da9] px-2 py-1">
          <span className="text-white text-[8px] font-semibold">View Bella&rsquo;s page</span>
        </div>
      </div>
    </div>
  )
}

export function MiniApp() {
  return (
    <div className="w-[160px] bg-white rounded-lg shadow-md border border-[#e0ebf3] p-3 text-center">
      <div className="text-[8px] uppercase tracking-wider text-[#94a3b8] font-semibold">Clinic app</div>
      <div className="mx-auto mt-1.5 w-8 h-8 rounded-full bg-[#dcfce7] flex items-center justify-center">
        <Check className="w-4 h-4 text-[#16a34a]" strokeWidth={3} />
      </div>
      <div className="mt-1.5 text-[10px] font-bold text-[#002e65]">Collection booked</div>
      <div className="text-[8px] text-[#94a3b8] mt-0.5">Pickup today, 2:30pm</div>
    </div>
  )
}

export function MiniMemorial() {
  return (
    <div className="relative w-[168px] h-[96px] rounded-lg overflow-hidden shadow-md border border-white/60">
      {/* browser chrome */}
      <div className="absolute top-0 inset-x-0 h-4 bg-white/90 z-10 flex items-center gap-1 px-2">
        <span className="w-1.5 h-1.5 rounded-full bg-[#ff5f57]" />
        <span className="w-1.5 h-1.5 rounded-full bg-[#febc2e]" />
        <span className="w-1.5 h-1.5 rounded-full bg-[#28c840]" />
      </div>
      <Image
        src="/favday/sunset.png"
        alt="Memorial page theme"
        fill
        className="object-cover"
        sizes="168px"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-2 text-center">
        <Heart className="mx-auto w-3.5 h-3.5 text-white fill-white/90 mb-0.5" />
        <div className="text-white text-[11px] font-bold drop-shadow leading-none">Max</div>
        <div className="text-white/85 text-[8px] drop-shadow">2009 &ndash; 2024</div>
      </div>
    </div>
  )
}

export function MiniKeepsakes() {
  return (
    <div className="flex flex-col items-center">
      <div className="flex items-center gap-2.5">
        <div className="relative w-[68px] h-[68px] rounded-lg overflow-hidden shadow-md border-2 border-white rotate-[-3deg]">
          <Image
            src="/keepsakes/paw-print.jpg"
            alt="Ink paw print keepsake"
            fill
            className="object-cover"
            sizes="68px"
          />
        </div>
        <div className="relative w-[68px] h-[68px] rounded-full overflow-hidden shadow-md border-2 border-white rotate-[3deg]">
          <Image
            src="/keepsakes/fur-locket.webp"
            alt="A clipping of fur in a corked glass vial"
            fill
            className="object-cover"
            sizes="68px"
          />
        </div>
      </div>
      <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-white shadow-sm border border-[#e0ebf3] px-2 py-0.5">
        <Check className="w-2.5 h-2.5 text-[#16a34a]" strokeWidth={3} />
        <span className="text-[8px] font-semibold text-[#002e65]">Ink paw print &middot; nose print &middot; fur vial</span>
      </div>
    </div>
  )
}

/* ──────────────────────────────────────────────────────────────────────── */
/* Owner-facing additions — the phases families experience that the clinic   */
/* journey doesn't need a visual for.                                        */

export function MiniQolly() {
  // The quality-of-life tracker families use to answer "is it time?"
  const bars = [3, 4, 4, 2, 3]
  return (
    <div className="w-[168px] bg-white rounded-lg shadow-md border border-[#e0ebf3] overflow-hidden">
      <div className="bg-[#4a86c5] px-3 py-1.5">
        <div className="text-white text-[9px] font-bold">How is Bella doing?</div>
      </div>
      <div className="p-2.5">
        <div className="flex items-end justify-between gap-1 h-[30px]">
          {bars.map((v, i) => (
            <div key={i} className="flex-1 flex flex-col justify-end h-full">
              <div
                className="w-full rounded-sm bg-[#4a86c5]"
                style={{ height: `${(v / 5) * 100}%`, opacity: 0.35 + i * 0.14 }}
              />
            </div>
          ))}
        </div>
        <div className="mt-1.5 flex items-center justify-between">
          <span className="text-[8px] text-[#94a3b8]">Last 5 days</span>
          <span className="text-[9px] font-bold text-[#002e65]">32 / 50</span>
        </div>
        <div className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-[#e6f0fa] px-2 py-0.5">
          <span className="w-1.5 h-1.5 rounded-full bg-[#4a86c5]" />
          <span className="text-[8px] font-semibold text-[#004da9]">Free to use, no sign-up</span>
        </div>
      </div>
    </div>
  )
}

export function MiniAshesHome() {
  // A premium scatter box on the doorstep — the ashes brought back by hand.
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-[112px]">
        {/* lid */}
        <div className="h-[13px] rounded-t-md bg-[#b99a6b] border border-b-0 border-[#9B7B4D] shadow-sm" />
        {/* body */}
        <div className="h-[46px] rounded-b-md bg-[#c9ad82] border border-t-0 border-[#9B7B4D] shadow-md flex items-center justify-center">
          {/* engraved name plate */}
          <div className="bg-white/95 rounded-sm px-2 py-1 text-center shadow-sm border border-[#e0d3ba]">
            <div className="text-[9px] font-bold text-[#6b5533] leading-none">Bella</div>
            <div className="text-[6px] text-[#a08c6b] mt-0.5 leading-none">2009 &ndash; 2024</div>
          </div>
        </div>
      </div>
      <div className="mt-2.5 inline-flex items-center gap-1 rounded-full bg-white shadow-sm border border-[#e0ebf3] px-2 py-0.5">
        <Check className="w-2.5 h-2.5 text-[#9B7B4D]" strokeWidth={3} />
        <span className="text-[8px] font-semibold text-[#002e65]">Hand-delivered to your door</span>
      </div>
    </div>
  )
}

export function MiniGriefSupport() {
  // The pet-loss resources families keep after their pet comes home.
  return (
    <div className="relative w-[180px] h-[92px]">
      <div className="absolute left-2 top-5 w-[88px] h-[64px] bg-white rounded-md shadow-md border border-[#e0ebf3] -rotate-3 p-2.5">
        <div className="w-5 h-1 rounded bg-[#9B7B4D] mb-1.5" />
        <div className="text-[9px] font-bold text-[#002e65] leading-tight">After they&rsquo;re gone</div>
        <div className="text-[7px] text-[#94a3b8] mt-0.5">Grief, in plain words</div>
      </div>
      <div className="absolute right-2 top-2 w-[88px] h-[68px] bg-[#9B7B4D] rounded-md shadow-lg rotate-3 p-2.5">
        <Heart className="w-3 h-3 text-white/85 mb-1" />
        <div className="text-[9px] font-bold text-white leading-tight">Talking to children</div>
        <div className="text-[7px] text-white/65 mt-0.5">Age by age</div>
      </div>
    </div>
  )
}
